import React, { useState, useEffect, useCallback } from 'react';
import { useInventoryModal } from '../context/InventoryModalContext';
import { useDialog } from '../context/DialogContext';
import { apiService } from '../services/api';
import { progressService } from '../services/progressService';
import { inventoryService, InventoryService } from '../services/inventoryService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import Header from '../components/Header';
import StepActions, { ActionItem } from '../components/StepActions';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import HistoryModal from '../components/HistoryModal';
import StepLoginPrompt from '../components/StepLoginPrompt';
import styles from '../styles/stepCommon.module.css';

interface Step4Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

interface BreadcrumbItem {
  label: string;
  path: string;
  active: boolean;
}

// Inventory types configuration
const INVENTORY_TYPES = [
  { key: 'resentments', label: 'Resentments' },
  { key: 'fears', label: 'Fears' },
  { key: 'harms', label: 'Harms Done' }
];

// Helper functions
function getDefaultItem(type: string): any {
  switch (type) {
    case 'resentments':
      return { who: '', why: '', feeling: '', impacts: [], myPart: '' };
    case 'fears':
      return { fear: '', why: '', impacts: [] };
    case 'harms':
      return { whoHarmed: '', how: '', why: '', defects: [], alternative: '' };
    default:
      return {};
  }
}

function isComplete(type: string, item: any): boolean {
  if (!item) return false;
  
  switch (type) {
    case 'resentments':
      return item.who && item.why && item.feeling && Array.isArray(item.impacts) && item.impacts.length > 0 && item.myPart;
    case 'fears':
      return item.fear && item.why && Array.isArray(item.impacts) && item.impacts.length > 0;
    case 'harms':
      return item.whoHarmed && item.how && item.why && Array.isArray(item.defects) && item.defects.length > 0 && item.alternative;
    default:
      return false;
  }
}

function formatVersionDate(date: Date) {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Type definitions
type InventoryData = Record<string, any[]>;
type VersionMap = Record<string, string[]>;
type CurrentVersionMap = Record<string, string>;
type VersionDataMap = Record<string, any[]>;

const Step4: React.FC<Step4Props> = ({ user, onLogin, onLogout }) => {
  const { openInventoryModal, setOnInventoryUpdate, setCurrentVersions, setInventoryStatuses: setContextInventoryStatuses } = useInventoryModal();
  const { showConfirm, showAlert, showHelp } = useDialog();
  
  // Action system state
  const [actions, setActions] = useState<ActionItem[]>([
    {
      id: 1,
      title: "Understand what Step Four in SAA is about and why it's important",
      description: "Learn how writing fearless moral inventories can help me uncover how beliefs and feelings drive my behavior",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setShowContentReader(true)
    },
    {
      id: 2,
      title: "How to work Step Four (guidance & video)",
      description: "Read guidance and watch video on how to work through Step Four",
      completed: false,
      buttons: [
        {
          text: "Read Guidance",
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: "Watch Video",
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: "Fill out inventories and schedule Step Five",
      description: "Facing my resentments, fears, and harms is how real healing begins.",
      completed: false
    },
    {
      id: 4,
      title: "Take Step Four: I have made a searching and fearless moral inventory of myself",
      description: "Complete the step by acknowledging your inventory work",
      completed: false
    }
  ]);
  
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [validationPopupType, setValidationPopupType] = useState<'action3'>('action3');
  const [action3Scheduled, setAction3Scheduled] = useState(false);
  const [action3Place, setAction3Place] = useState(false);

  // Inventory status tracking
  const [inventoryStatuses, setInventoryStatuses] = useState<Array<{
    type: string;
    label: string;
    total: number;
    complete: number;
    isCompleted: boolean;
  }>>([]);

  // Existing inventory state
  const [auth, setAuth] = useState(false);
  const [currentType, setCurrentType] = useState('resentments');
  const [loading, setLoading] = useState(true);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();
  const [importStatus, setImportStatus] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [showContentReader, setShowContentReader] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);

  const [versions, setVersions] = useState<VersionMap>({ resentments: [], fears: [], harms: [] });
  const [currentVersion, setCurrentVersion] = useState<CurrentVersionMap>({ resentments: '', fears: '', harms: '' });
  const [versionData, setVersionData] = useState<VersionDataMap>({});

  // Load data from API - depends on user to avoid race with auth/encryption key init
  useEffect(() => {
    const initializeData = async (retryCount = 0): Promise<void> => {
      try {
        setLoading(true);
        clearError();

        // Check authentication
        if (!apiService.isAuthenticated()) {
          setAuth(false);
          setLoading(false);
          return;
        }

        setAuth(true);

        // Wait for user before loading - prevents race where getInventory runs before
        // encryption key is ready (which causes silent empty data on first visit)
        if (!user) {
          setLoading(false);
          return;
        }

        // Load versions for all inventory types
        const [resentmentVersions, fearVersions, harmVersions] = await Promise.all([
          InventoryService.listInventoryVersions('resentments'),
          InventoryService.listInventoryVersions('fears'),
          InventoryService.listInventoryVersions('harms')
        ]);

        const newVersions: VersionMap = {
          resentments: resentmentVersions,
          fears: fearVersions,
          harms: harmVersions
        };

        setVersions(newVersions);

        // Set current versions (first available or empty string)
        const newCurrentVersion: CurrentVersionMap = {
          resentments: resentmentVersions.length > 0 ? resentmentVersions[0] : '',
          fears: fearVersions.length > 0 ? fearVersions[0] : '',
          harms: harmVersions.length > 0 ? harmVersions[0] : ''
        };

        setCurrentVersion(newCurrentVersion);

        // Load data for current versions (getInventory needs decryption - can fail if key not ready)
        const newVersionData: VersionDataMap = {};
        let anyLoadFailed = false;
        
        for (const type of ['resentments', 'fears', 'harms'] as const) {
          if (newCurrentVersion[type]) {
            try {
              const items = await InventoryService.getInventory(type, newCurrentVersion[type]);
              const filteredItems = items.filter(item => item._type !== 'completion_status');
              newVersionData[newCurrentVersion[type]] = filteredItems;
            } catch (error) {
              // Don't silently treat as empty - may be encryption key timing
              anyLoadFailed = true;
              newVersionData[newCurrentVersion[type]] = [];
            }
          }
        }

        // Retry once if we have versions but all loads failed (likely encryption key not ready yet)
        const hasVersions = Object.values(newCurrentVersion).some(v => v);
        if (anyLoadFailed && hasVersions && retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 500));
          return initializeData(1);
        }

        setVersionData(newVersionData);
        setLoading(false);

      } catch (error: any) {
        console.error('Error initializing data:', error);
        handleError(error, 'Failed to load data. Please try refreshing the page.');
        setLoading(false);
      }
    };

    initializeData();
  }, [user]);

  // Initialize action system progress
  const initializeActionProgress = async () => {
    try {
      // Initialize SAA progress
      await progressService.initializeProgress();
      
      // Load existing step progress
      const stepProgress = await progressService.getStepProgress('SAA', '12-steps', 4).catch(() => null);
      
      if (stepProgress) {
        setStepData(stepProgress);
        
        if (stepProgress.data) {
          if (stepProgress.data.actions) {
            // Update actions with saved progress
            setActions(prevActions => {
              const updatedActions = prevActions.map(action => {
                const savedAction = stepProgress.data.actions.find((a: any) => a.id === action.id);
                return savedAction ? { ...action, completed: savedAction.completed } : action;
              });
              
              // Auto-collapse completed actions
              const completedActionIds = updatedActions
                .filter(action => action.completed)
                .map(action => action.id);
              
              setCollapsedActions(new Set(completedActionIds));
              
              return updatedActions;
            });
          }
          
          // Load action 3 checkbox states
          if (stepProgress.data.action3_scheduled !== undefined) {
            setAction3Scheduled(stepProgress.data.action3_scheduled);
          }
          if (stepProgress.data.action3_place !== undefined) {
            setAction3Place(stepProgress.data.action3_place);
          }
        }
      }
        
      // Check date tracking preference
      try {
        const journeyData = await progressService.getJourneyProgress();
        setIsOptedOut(journeyData.opted_out);
      } catch (error) {
        console.error('Error loading date tracking preference:', error);
      }
    } catch (err) {
      console.error('Error initializing action progress:', err);
    }
  };

  useEffect(() => {
    if (user) {
      initializeActionProgress();
    }
  }, [user]);

  // API wrapper functions - now using encrypted inventory service
  const saveInventoryToAPI = async (inventoryType: string, version: string, items: any[]) => {
    try {
      await InventoryService.saveInventory(inventoryType, version, items);
    } catch (error) {
      console.error('Error saving to API:', error);
      throw error;
    }
  };

  const deleteInventoryFromAPI = async (inventoryType: string, version: string) => {
    try {
      await InventoryService.deleteInventory(inventoryType, version);
    } catch (error) {
      console.error('Error deleting from API:', error);
      throw error;
    }
  };

  // Get current data for the selected version
  const getCurrentData = () => {
    return versionData[currentVersion[currentType]] || [];
  };

  // Check if there are no versions for the current inventory type
  const hasNoVersions = () => {
    return !versions[currentType] || versions[currentType].length === 0;
  };

  // Calculate counts for current inventory
  const getInventoryCounts = () => {
    const currentData = getCurrentData();
    const total = currentData.length;
    const complete = currentData.filter((item: any) => isComplete(currentType, item)).length;
    return { total, complete };
  };

  // Check if all three inventories have at least one entry
  const hasAllInventoriesStarted = () => {
    const resentmentsCount = versionData[currentVersion.resentments]?.length || 0;
    const fearsCount = versionData[currentVersion.fears]?.length || 0;
    const harmsCount = versionData[currentVersion.harms]?.length || 0;
    
    return resentmentsCount > 0 && fearsCount > 0 && harmsCount > 0;
  };

  // Calculate inventory statuses for all types
  const calculateInventoryStatuses = useCallback(async (forceRefreshData = false) => {
    const statuses = await Promise.all(INVENTORY_TYPES.map(async type => {
      const currentVersionKey = currentVersion[type.key];
      
      // If forcing refresh, get fresh data from API
      let data: any[] = [];
      if (forceRefreshData && currentVersionKey && user) {
        try {
          const items = await InventoryService.getInventory(type.key, currentVersionKey);
          data = items.filter(item => item._type !== 'completion_status');
        } catch (error) {
          console.error(`Error refreshing ${type.key} data:`, error);
          data = versionData[currentVersionKey] || [];
        }
      } else {
        data = currentVersionKey ? (versionData[currentVersionKey] || []) : [];
      }
      
      const total = data.length;
      const complete = data.filter((item: any) => isComplete(type.key, item)).length;
      
      // Load completion status from API
      let isCompleted = false;
      if (currentVersionKey && user) {
        try {
          const completionData = await InventoryService.getInventoryCompletion(type.key, currentVersionKey);
          isCompleted = completionData.completed || false;
        } catch (error) {
          // Silently handle errors
          isCompleted = false;
        }
      } else {
        isCompleted = false;
      }
      
      const status = {
        type: type.key,
        label: type.label,
        total,
        complete,
        isCompleted
      };
      
      return status;
    }));
    
    setInventoryStatuses(statuses);
  }, [currentVersion, versionData, user]);

  // Initial load of inventory statuses
  useEffect(() => {
    if (user && Object.keys(currentVersion).length > 0) {
      calculateInventoryStatuses();
    }
  }, [user, currentVersion, calculateInventoryStatuses]);

  // Force re-render when inventory statuses change
  useEffect(() => {
    // Update the context with the current inventory statuses
    setContextInventoryStatuses(inventoryStatuses);
  }, [inventoryStatuses, setContextInventoryStatuses]);

  // Set up inventory update callback and current versions
  useEffect(() => {
    setOnInventoryUpdate(() => {
      return async (inventoryType: string) => {
        // Only reload the specific inventory type that was updated
        try {
          const versionKey = currentVersion[inventoryType as 'resentments' | 'fears' | 'harms'];
          
          if (versionKey) {
            try {
              const items = await InventoryService.getInventory(inventoryType, versionKey);
              // Filter out completion status items when reloading inventory data
              const filteredItems = items.filter(item => item._type !== 'completion_status');
              
              // Update only this inventory type's data
              setVersionData(prev => ({
                ...prev,
                [versionKey]: filteredItems
              }));
            } catch (error) {
              // Silently handle errors
              setVersionData(prev => ({
                ...prev,
                [versionKey]: []
              }));
            }
          }
          
          // Recalculate inventory statuses with the updated data
          await calculateInventoryStatuses(true);
        } catch (error) {
          // Silently handle errors
        }
      };
    });

    // Set current versions in context for the modal to use
    setCurrentVersions(currentVersion);
  }, [setOnInventoryUpdate, setCurrentVersions, currentVersion, calculateInventoryStatuses]);

  // Handle Green Book button click
  const handleGreenBook = () => {
    setShowContentReader(true);
  };

  // Handle guidance button click
  const handleGuidanceClick = () => {
    setIsGuidanceOpen(true);
  };

  // Action system helper functions
  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    // For actions 1 and 2, require sequential completion
    if (actionIndex < 2) {
      const currentActionIndex = getCurrentActionIndex();
      return actionIndex <= currentActionIndex;
    }
    // Action 3 (inventories) is always available for checking/unchecking
    if (actionIndex === 3) {
      return true;
    }
    // Action 4 requires Action 3 to be completed
    if (actionIndex === 4) {
      const action3 = actions.find(a => a.id === 3);
      return action3 ? action3.completed : false;
    }
    return true;
  };

  // Check if action 3 can be completed (all inventories must be completed AND both checkboxes checked)
  const canCompleteAction3 = () => {
    const allInventoriesCompleted = inventoryStatuses.every(status => status.isCompleted);
    const bothCheckboxesChecked = action3Scheduled && action3Place;
    return allInventoriesCompleted && bothCheckboxesChecked;
  };

  // Check if any action can be completed (for StepActions component)
  const canCompleteAction = (actionId: number) => {
    if (actionId === 3) {
      return canCompleteAction3();
    }
    if (actionId === 4) {
      // Action 4 can be completed if Action 3 is completed
      const action3 = actions.find(a => a.id === 3);
      return action3 ? action3.completed : false;
    }
    return true; // Actions 1 and 2 can always be completed
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      console.error('User not logged in. Please log in.');
      return;
    }

    try {
      // Special validation for action 3 - require all inventories to be completed AND both checkboxes checked
      const action3 = updatedActions.find(a => a.id === 3);
      if (action3 && action3.completed) {
        const allInventoriesCompleted = inventoryStatuses.every(status => status.isCompleted);
        const bothCheckboxesChecked = action3Scheduled && action3Place;
        
        if (!allInventoriesCompleted || !bothCheckboxesChecked) {
          setValidationPopupType('action3');
          setShowValidationPopup(true);
          return; // Don't update actions
        }
      }

      // Update local state
      setActions(updatedActions);

      // Save to backend with consistent structure, preserving checkbox data
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 4,
        step_title: 'Step Four',
        completed: allCompleted,
        data: { 
          actions: updatedActions,
          action3_scheduled: action3Scheduled,
          action3_place: action3Place
        }
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }

    } catch (error: any) {
      console.error('Error updating action progress:', error);
      handleError(error, 'Failed to save progress. Please try again.');
      // Revert the action state on error
      setActions(prevActions => 
        prevActions.map(action => {
          const updatedAction = updatedActions.find(a => a.id === action.id);
          return updatedAction ? { ...action, completed: updatedAction.completed } : action;
        })
      );
    }
  };

  const handleActionButtonClick = (action: ActionItem, e: React.MouseEvent) => {
    e.preventDefault();
    if (action.buttonAction) {
      action.buttonAction();
    }
  };

  const toggleActionCollapse = (actionId: number) => {
    setCollapsedActions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(actionId)) {
        newSet.delete(actionId);
      } else {
        newSet.add(actionId);
      }
      return newSet;
    });
  };

  const handleAction3CheckboxChange = async (checkboxType: 'scheduled' | 'place') => {
    if (!user) {
      console.error('User not logged in. Please log in.');
      return;
    }

    const newValue = checkboxType === 'scheduled' ? !action3Scheduled : !action3Place;
    
    try {
      // Update the checkbox state
      if (checkboxType === 'scheduled') {
        setAction3Scheduled(newValue);
      } else {
        setAction3Place(newValue);
      }

      // Save to API with correct individual checkbox values
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 4,
        step_title: 'Step Four',
        completed: false,
        data: {
          action3_scheduled: checkboxType === 'scheduled' ? newValue : action3Scheduled,
          action3_place: checkboxType === 'place' ? newValue : action3Place
        }
      });

      // Note: Action 3 completion is now manual - user must click the "Completed" checkbox
      // Auto-completion has been removed to give users control over when they mark the step complete

    } catch (error) {
      console.error('Error updating action 3 checkbox:', error);
      // Revert the checkbox state on error
      if (checkboxType === 'scheduled') {
        setAction3Scheduled(!newValue);
      } else {
        setAction3Place(!newValue);
      }
    }
  };

  // Custom confirm dialog
  const openConfirm = (message: string, action: () => void, singleButton: boolean = false) => {
    if (singleButton) {
      showAlert({
        message,
        variant: 'info'
      });
    } else {
      showConfirm({
        message,
        onConfirm: action,
        variant: 'info'
      });
    }
  };
  
  // Show informational dialog (single button)
  const showInfo = (message: string) => {
    showAlert({
      message,
      variant: 'info'
    });
  };

  // Help dialog
  const openHelp = (fieldId: string) => {
    showHelp({
      title: 'Help',
      content: 'Help content not available for this field.'
    });
  };

  // Breadcrumbs
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Four', path: '/saa/12-steps/step-4', active: true }
  ];

  if (!auth) {
    return (
      <div className={styles.step4Page}>
        <Header 
          breadcrumbs={breadcrumbs}
          user={user}
          onLogin={onLogin}
          onLogout={onLogout}
        />
        <main className={styles.mainContent}>
          <div className={styles.pageHeader}>
            <div className={styles.stepTitleContainer}>
              <h1 className={styles.stepTitle}>Step Four</h1>
              {stepData && (
                <HistoryButton
                  stepNumber={4}
                  stepTitle="Step Four"
                  stepData={stepData}
                  isOptedOut={isOptedOut}
                  onStepUpdated={() => setIsHistoryModalOpen(true)}
                />
              )}
            </div>
            <p className={styles.stepSubtitle}>
              Made a searching and fearless moral inventory of ourselves.
            </p>
          </div>
          <StepLoginPrompt onLogin={onLogin} />
        </main>
      </div>
    );
  }



  return (
    <div className={styles.step4Page}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogin={onLogin}
        onLogout={onLogout}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Four</h1>
            {stepData && (
              <HistoryButton
                stepNumber={4}
                stepTitle="Step Four"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            Made a searching and fearless moral inventory of ourselves.
          </p>
        </div>

        {loading && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!loading && !errorMessage && (
          <>
            {/* Action System */}
        <div style={{ marginBottom: '1rem' }}>
          <StepActions
            key={`step-actions-${inventoryStatuses.length}-${JSON.stringify(inventoryStatuses.map(s => `${s.type}-${s.total}-${s.isCompleted}`))}`}
            actions={actions}
            onActionChange={handleActionChange}
            stepNumber={4}
            canCompleteAction={canCompleteAction}
            renderActionContent={(action, index) => {
              if (action.id === 3) {
                const getStatusText = (status: any): string => {
                  if (status.total === 0) {
                    return 'not started';
                  } else if (status.isCompleted) {
                    return `completed (${status.total})`;
                  } else {
                    return `in progress (${status.total})`;
                  }
                };

                const getStatusColor = (status: any): string => {
                  if (status.total === 0) {
                    return '#9ca3af'; // gray
                  } else if (status.isCompleted) {
                    return '#10b981'; // green
                  } else {
                    return '#fbbf24'; // yellow
                  }
                };

                return (
                  <>
                    <div className={styles.action3Content}>
                      {/* Inventory Status List */}
                      <div className={styles.inventoryStatusList}>
                        {inventoryStatuses.map((status) => (
                          <div 
                            key={status.type} 
                            className={styles.inventoryStatusItem}
                            onClick={() => openInventoryModal(status.type)}
                            style={{ cursor: 'pointer' }}
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openInventoryModal(status.type);
                              }
                            }}
                          >
                            <span className={styles.inventoryLabel}>
                              {status.label}
                            </span>
                            <span className={styles.inventoryStatus}>
                              <span 
                                className={styles.statusBullet}
                                style={{ color: getStatusColor(status) }}
                              >
                                •
                              </span>
                              <span className={styles.statusText}>
                                {getStatusText(status)}
                              </span>
                              <span className={styles.chevronIcon}>→</span>
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Scheduling Section */}
                      <div className={styles.schedulingSection}>
                        <div className={styles.schedulingTitle}>
                          Schedule Step Five with my sponsor
                          <span className={styles.schedulingSubtext}>(while I am still working on my inventories)</span>
                        </div>
                        <div className={styles.schedulingCheckboxes}>
                          <label className={styles.schedulingCheckboxLabel}>
                            <input
                              type="checkbox"
                              checked={action3Scheduled}
                              onChange={() => handleAction3CheckboxChange('scheduled')}
                              className={styles.schedulingCheckbox}
                            />
                            <span className={styles.checkboxText}>
                              <span className={styles.checkboxTextFull}>Have scheduled a date and time</span>
                              <span className={styles.checkboxTextMobile}>Scheduled date and time</span>
                            </span>
                          </label>
                          <label className={styles.schedulingCheckboxLabel}>
                            <input
                              type="checkbox"
                              checked={action3Place}
                              onChange={() => handleAction3CheckboxChange('place')}
                              className={styles.schedulingCheckbox}
                            />
                            <span className={styles.checkboxText}>
                              <span className={styles.checkboxTextFull}>Have picked a place to meet (in-person or online)</span>
                              <span className={styles.checkboxTextMobile}>Picked place to meet</span>
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </>
                );
              }
              return null;
            }}
          />
        </div>

        {/* Congratulations Message */}
        {actions.every(action => action.completed) && (
          <>
            <div style={{ 
              backgroundColor: '#f0fff4', 
              border: '1px solid #9ae6b4', 
              borderRadius: '8px', 
              padding: '16px', 
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#22543d' }}>🎉 Congratulations!</h3>
              <p style={{ margin: 0, color: '#22543d' }}>
                You have completed all actions for Step Four and can move on to Step Five.
              </p>
            </div>
            <div className={styles.stepNavigation}>
              <div className={styles.navigationLinks}>
                <a href="/saa/12-steps/step-3" style={{ 
                  color: '#007bff',
                  textDecoration: 'none',
                  fontWeight: '500',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef'
                }}>
                  {'<<'} Step Three
                </a>
                <span style={{ flex: 1 }}></span>
                <a href="/saa/12-steps/step-5" style={{ 
                  color: '#007bff',
                  textDecoration: 'none',
                  fontWeight: '500',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef'
                }}>
                  Step Five {'>>'}
                </a>
              </div>
            </div>
          </>
        )}

        {/* Navigation Links */}
        {!actions.every(action => action.completed) && (
          <div className={styles.stepNavigation}>
            <div className={styles.navigationLinks}>
              <a href="/saa/12-steps/step-3" style={{ 
                color: '#007bff',
                textDecoration: 'none',
                fontWeight: '500',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                background: '#f8f9fa',
                border: '1px solid #e9ecef'
              }}>
                {'<<'} Step Three
              </a>
              <span style={{ flex: 1 }}></span>
              <a href="/saa/12-steps/step-5" style={{ 
                color: '#007bff',
                textDecoration: 'none',
                fontWeight: '500',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                background: '#f8f9fa',
                border: '1px solid #e9ecef'
              }}>
                Step Five {'>>'}
              </a>
            </div>
          </div>
        )}
          </>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={showContentReader}
        onClose={() => setShowContentReader(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-four start>>"
        endTag="<<step-four end>>"
      />
      
      {/* ContentReader for Step Four Guidance */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_4_Guidance.md"
        startTag="<<working-step-four start>>"
        endTag="<<working-step-four end>>"
        title="Guidance on working Step Four"
      />

      {/* Validation Popup Modal */}
      {showValidationPopup && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: 24, 
            borderRadius: 8, 
            maxWidth: 400, 
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>Action Not Complete</h3>
            <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: 1.4 }}>
              {validationPopupType === 'action3' 
                ? 'You need to complete all three inventories (Resentments, Fears, and Harms Done) and check both scheduling boxes to mark this action complete.'
                : 'Click both checkboxes to confirm you have scheduled a date and time and picked a specific place.'
              }
            </p>
            <button
              onClick={() => setShowValidationPopup(false)}
              style={{ 
                background: '#007bff', 
                color: 'white', 
                border: 'none', 
                borderRadius: 4, 
                padding: '8px 24px', 
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Ok
            </button>
          </div>
        </div>
      )}
      
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="AZGY_cEnF2Y"
        title="Step Four Video Guide"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={4}
        stepTitle="Step Four"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          // Refresh step data
          if (user) {
            initializeActionProgress();
          }
        }}
      />
    </div>
  );
};

export default Step4; 
